import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import { Separator } from "components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Textarea } from "components/ui/textarea";
import {
  Trash,
  ArrowSquareOut,
  FloppyDisk,
} from "@phosphor-icons/react";
import { Asset, AssetStatus, UpdateAssetDto } from "shared/types/asset";
import { useUpdateAsset, useDeleteAsset } from "hooks/useAssets";
import { AssetMaintenanceSection } from "components/assets/AssetMaintenanceSection";
import { AssetQRCodeSection } from "components/assets/AssetQRCodeSection";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const STATUS_LABELS: Record<AssetStatus, string> = {
  [AssetStatus.ACTIVE]: "Actief",
  [AssetStatus.INACTIVE]: "Inactief",
  [AssetStatus.MAINTENANCE]: "In onderhoud",
  [AssetStatus.DEFECT]: "Defect",
  [AssetStatus.RETIRED]: "Buiten dienst",
  [AssetStatus.DECOMMISSIONED]: "Ontmanteld",
  [AssetStatus.PENDING_INSTALLATION]: "In afwachting",
};

interface AssetQuickEditSheetProps {
  asset: Asset | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const AssetQuickEditSheet = ({
  asset,
  open,
  onOpenChange,
  onSuccess,
}: AssetQuickEditSheetProps) => {
  const navigate = useNavigate();
  const { updateAsset, isUpdating } = useUpdateAsset();
  const { deleteAsset, isDeleting } = useDeleteAsset();

  const [formData, setFormData] = useState<Partial<UpdateAssetDto>>({});
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (asset) {
      setFormData({
        name: asset.name,
        identifier: asset.identifier,
        status: asset.status,
        location: asset.location,
        installationDate: asset.installationDate,
        warrantyExpiryDate: asset.warrantyExpiryDate,
        notes: asset.notes,
        assetCategoryId: asset.assetCategoryId,
      });
      setConfirmDelete(false);
    }
  }, [asset]);

  if (!asset) return null;

  const handleSave = async () => {
    try {
      await updateAsset(asset.id, formData as UpdateAssetDto);
      toast.success("Voorziening bijgewerkt");
      onSuccess();
    } catch (error) {
      toast.error("Kon voorziening niet bijwerken");
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    try {
      await deleteAsset(asset.id);
      toast.success("Voorziening verwijderd");
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      toast.error("Kon voorziening niet verwijderen");
    }
  };

  const formatDateForInput = (date?: Date | string) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>{asset.name}</DialogTitle>
            <Badge
              variant="secondary"
              style={{
                backgroundColor: asset.assetCategory?.color || undefined,
                color: asset.assetCategory?.color ? "#fff" : undefined,
              }}
            >
              {asset.assetCategory?.name}
            </Badge>
          </div>
          <DialogDescription>
            Bewerk de gegevens van deze voorziening
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic fields - 2 column grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Naam</Label>
              <Input
                value={formData.name || ""}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Identificatie</Label>
              <Input
                value={formData.identifier || ""}
                onChange={(e) =>
                  setFormData({ ...formData, identifier: e.target.value })
                }
                placeholder="Serienummer, referentie..."
              />
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={formData.status || AssetStatus.ACTIVE}
                onValueChange={(v) =>
                  setFormData({ ...formData, status: v as AssetStatus })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Locatie</Label>
              <Input
                value={formData.location || ""}
                onChange={(e) =>
                  setFormData({ ...formData, location: e.target.value })
                }
                placeholder="bv. Kelder, 3de verdieping..."
              />
            </div>

            <div className="space-y-2">
              <Label>Installatiedatum</Label>
              <Input
                type="date"
                value={formatDateForInput(formData.installationDate)}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    installationDate: e.target.value
                      ? new Date(e.target.value)
                      : undefined,
                  })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Garantie tot</Label>
              <Input
                type="date"
                value={formatDateForInput(formData.warrantyExpiryDate)}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    warrantyExpiryDate: e.target.value
                      ? new Date(e.target.value)
                      : undefined,
                  })
                }
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Opmerkingen</Label>
            <Textarea
              value={formData.notes || ""}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              rows={3}
            />
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              disabled={isUpdating}
              className="flex-1"
            >
              <FloppyDisk className="mr-1 h-4 w-4" />
              {isUpdating ? "Opslaan..." : "Opslaan"}
            </Button>
          </div>

          <Separator />

          {/* Maintenance Section */}
          <AssetMaintenanceSection assetId={asset.id} />

          <Separator />

          {/* QR Code Section */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium">QR Code</h4>
            <AssetQRCodeSection assetId={asset.id} assetName={asset.name} />
          </div>

          <Separator />

          {/* Actions */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/asset/${asset.id}`)}
            >
              <ArrowSquareOut className="mr-1 h-4 w-4" />
              Volledig bewerken
            </Button>
            <Button
              variant={confirmDelete ? "destructive" : "ghost"}
              size="sm"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              <Trash className="mr-1 h-4 w-4" />
              {confirmDelete
                ? "Bevestig verwijdering"
                : isDeleting
                  ? "Verwijderen..."
                  : "Verwijderen"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
